import React from "react";
import { useNavigate } from "react-router-dom";
import { usePoll } from "@/hooks/usePoll";
import { useLive } from "@/store/LiveContext";
import { endpoints } from "@/lib/api";
import { inr, inrL, num } from "@/lib/format";
import { Sparkles } from "lucide-react";

function Cluster({ title, accent, children, testid }) {
  return (
    <div className="flex flex-col px-3 lg:px-4 py-1.5 border-l-2" style={{ borderColor: accent }} data-testid={testid}>
      <span className="text-[9px] font-bold tracking-[0.12em] uppercase" style={{ color: accent }}>
        {title}
      </span>
      <div className="flex items-center gap-3 lg:gap-4 mt-1">{children}</div>
    </div>
  );
}

function Metric({ label, value, onClick, testid }) {
  return (
    <button onClick={onClick} data-testid={testid} className="text-left group">
      <div className="text-[9px] uppercase tracking-wide text-slate-400 group-hover:text-slate-600">{label}</div>
      <div className="text-[13px] font-bold font-mono text-slate-900 group-hover:text-blue-600 leading-tight whitespace-nowrap">
        {value}
      </div>
    </button>
  );
}

export default function Ribbons() {
  const navigate = useNavigate();
  const { data } = usePoll(endpoints.ribbons, 5000);
  const { data: live } = useLive();
  // Prefer the WebSocket push (instant); fall back to REST polling.
  const r = live?.ribbons || data?.ribbons;
  const brief = data?.ai_brief;

  if (!r) {
    return <div className="h-[58px] border-b border-slate-200 bg-white" />;
  }
  const { production: p, economics: e, sustainability: s, alerts: a } = r;

  return (
    <div className="sticky top-0 z-40 bg-white border-b border-slate-200 shadow-sm" data-testid="persistent-ribbons">
      <div className="flex items-stretch overflow-x-auto divide-x divide-transparent">
        <Cluster title="Production" accent="#2563eb" testid="ribbon-production">
          <Metric label="Milk" value={`${num(p.milk_klpd)} KLPD`} onClick={() => navigate("/")} />
          <Metric label="Yield" value={`${p.yield_pct}%`} onClick={() => navigate("/")} />
          <Metric label="OEE" value={`${p.oee_pct}%`} onClick={() => navigate("/")} />
        </Cluster>

        <Cluster title="Economics" accent="#10b981" testid="ribbon-economics">
          <Metric label="Utility/L" value={inrL(e.utility_per_l)} onClick={() => navigate("/economics")} testid="ribbon-utility-per-l" />
          <Metric label="Steam/L" value={inrL(e.steam_per_l)} onClick={() => navigate("/twin/boiler")} />
          <Metric label="Refr/L" value={inrL(e.refrigeration_per_l)} onClick={() => navigate("/twin/refrigeration")} />
          <Metric label="Margin/L" value={inrL(e.margin_per_l)} onClick={() => navigate("/economics")} />
          <Metric label="Opp/Yr" value={inr(e.opportunity_yr)} onClick={() => navigate("/ai")} />
        </Cluster>

        <Cluster title="Sustainability" accent="#059669" testid="ribbon-sustainability">
          <Metric label="Energy" value={`${s.energy_intensity} kWh/L`} onClick={() => navigate("/sustainability")} />
          <Metric label="Water" value={`${s.water_intensity} L/L`} onClick={() => navigate("/sustainability")} />
          <Metric label="Carbon" value={`${s.carbon_intensity} kgCO₂/L`} onClick={() => navigate("/sustainability")} />
          <Metric label="Renew" value={`${s.renewable_pct}%`} onClick={() => navigate("/sustainability")} />
        </Cluster>

        <Cluster title="Alerts & Risk" accent="#ef4444" testid="ribbon-alerts">
          <Metric label="Crit" value={a.critical} onClick={() => navigate("/ai")} />
          <Metric label="High" value={a.high} onClick={() => navigate("/ai")} />
          <Metric label="Med" value={a.medium} onClick={() => navigate("/ai")} />
          <Metric label="Fin Risk" value={inr(a.financial_risk_yr)} onClick={() => navigate("/ai")} />
        </Cluster>

        <button
          onClick={() => navigate("/ai")}
          data-testid="ribbon-ai-brief"
          className="flex items-center gap-2 px-4 py-1.5 border-l-2 border-violet-500 hover:bg-violet-50 transition-colors min-w-[260px] flex-1 text-left"
        >
          <Sparkles size={14} className="text-violet-500 shrink-0" />
          <div className="overflow-hidden">
            <span className="text-[9px] font-bold tracking-[0.12em] uppercase text-violet-500">AI Brief</span>
            <p className="text-[11px] text-slate-700 truncate leading-tight">
              {brief?.text ? brief.text.replace(/[#*🔴🟠🟢]/g, "").split("\n")[0].trim() : "Open AI Operations Center to generate the executive brief."}
            </p>
          </div>
        </button>
      </div>
    </div>
  );
}
